import { __assign, __read } from "tslib";
import { BackendEventType, } from '../types';
import { getBatchReportURL, getContentLength, getDataLength, pick, serialize, strSize } from '../utils';
import { log } from '../utils/logger';
import uuid from '../utils/uuid';
import { DEVICE_ID_STORAGE_KEY, RANGERS_PAGE_SESSION_ID, RANGERS_PAGE_TIMING } from './constants';
import { AppMethod, PageMethod, SDKMethod, } from './types';
import { getDummyEnvInfo, getPid, hookComponentLifeCycles, hookLifeCycles, hookObjectMethods, makeError, makeErrorFromRejection, } from './utils';
var Backend = /** @class */ (function () {
    function Backend(options, callback) {
        this.originalAppFn = App;
        this.originalPageFn = Page;
        this.reportUrl = '';
        this.deviceId = '';
        this.appSessionId = '';
        this.pageSessionId = '';
        this.capturedContext = null;
        this.taskStack = [];
        this.taskTracks = {};
        this.options = options;
        this.pid = options.pid;
        this.callback = callback;
        // init later so the subclass can inject sdk
    }
    Backend.prototype.init = function () {
        var _this = this;
        log('backend init');
        this.transport = this.setupTransport();
        this.collectDeviceId();
        App = this.hookAppFn(App);
        Page = this.hookPageFn(Page);
        Component = this.hookComponentFn(Component);
        this.hookSdk();
        if (this.sdk.onNetworkStatusChange) {
            this.sdk.onNetworkStatusChange(function (options) {
                _this.callback({
                    ev_type: BackendEventType.NETWORK_STATUS,
                    data: {
                        network_type: options.networkType,
                    },
                });
            });
        }
    };
    Backend.prototype.sendEvent = function (ev) {
        var _a;
        (_a = this.transport) === null || _a === void 0 ? void 0 : _a.sendEvent(ev);
    };
    Backend.prototype.collectEnvInfo = function () {
        var _this = this;
        Promise.all([
            new Promise(function (resolve, reject) {
                _this.sdk.getSystemInfo({
                    success: resolve,
                    fail: reject,
                });
            }),
            new Promise(function (resolve, reject) {
                _this.sdk.getNetworkType({
                    success: resolve,
                    fail: reject,
                });
            }),
        ])
            .then(function (_a) {
            var _b = __read(_a, 2), systemInfo = _b[0], networkStatus = _b[1];
            if (systemInfo && networkStatus) {
                _this.callback({
                    ev_type: BackendEventType.ENV_INFO,
                    data: _this.normalizeEnvInfo(systemInfo, networkStatus),
                });
            }
        })
            .catch(function () {
            _this.callback({
                ev_type: BackendEventType.ENV_INFO,
                data: getDummyEnvInfo(),
            });
        });
    };
    Backend.prototype.collectFinderConfig = function () {
        var _this = this;
        var _a;
        (_a = this.options.finderInstance) === null || _a === void 0 ? void 0 : _a.getToken(function (token) {
            _this.callback({
                ev_type: BackendEventType.FINDER_CONFIG,
                data: {
                    user_unique_id: token.user_unique_id,
                    ssid: token.ssid,
                },
            });
        });
    };
    Backend.prototype.collectTesterSdkVersion = function () {
        var _this = this;
        var _a, _b, _c;
        // https://bytedance.feishu.cn/docs/doccnBJe87xLfCMJ6w5hC8wgBic
        var vids = (_a = this.options.finderInstance) === null || _a === void 0 ? void 0 : _a.getAbSdkVersion();
        if (vids) {
            this.callback({
                ev_type: BackendEventType.TESTER_SDK_VERSION,
                data: {
                    ab_sdk_version: vids,
                },
            });
        }
        (_c = (_b = this.options.finderInstance) === null || _b === void 0 ? void 0 : _b.onAbSdkVersionChange) === null || _c === void 0 ? void 0 : _c.call(_b, function (vids) {
            _this.callback({
                ev_type: BackendEventType.TESTER_SDK_VERSION,
                data: {
                    ab_sdk_version: vids,
                },
            });
        });
    };
    Backend.prototype.getStorage = function (key) {
        return this.sdk.getStorageSync(key);
    };
    Backend.prototype.setStorage = function (key, data) {
        return this.sdk.setStorageSync(key, data);
    };
    Backend.prototype.setupTransport = function () {
        var _this = this;
        this.reportUrl = getBatchReportURL(this.options.reportDomain, this.options.reportPath);
        return {
            sendEvent: function (event) {
                _this.sdk.request({
                    url: _this.reportUrl,
                    method: 'POST',
                    data: event,
                });
            },
            request: function (options) {
                _this.sdk.request(options);
            },
        };
    };
    Backend.prototype.hookAppFn = function (App) {
        var that = this;
        var callback = this.callback;
        return hookLifeCycles(App, [AppMethod.onLaunch, AppMethod.onShow, AppMethod.onHide, AppMethod.onError, AppMethod.onUnhandledRejection], function (method, originalHandler) {
            function wrappedAppHandler(options) {
                log('App', method);
                switch (method) {
                    case AppMethod.onShow: {
                        var appSessionId = uuid();
                        that.appSessionId = appSessionId;
                        callback({
                            ev_type: BackendEventType.APP_SESSION,
                            data: {
                                appSessionId: appSessionId,
                            },
                        });
                        break;
                    }
                    case AppMethod.onError: {
                        callback({
                            ev_type: BackendEventType.GLOBAL_JS_ERROR,
                            data: makeError(options),
                        });
                        break;
                    }
                    case AppMethod.onUnhandledRejection: {
                        makeErrorFromRejection(options).then(function (data) {
                            callback({
                                ev_type: BackendEventType.GLOBAL_JS_ERROR,
                                data: data,
                            });
                        });
                        break;
                    }
                    default: {
                        break;
                    }
                }
                originalHandler === null || originalHandler === void 0 ? void 0 : originalHandler.call(this, options);
            }
            return wrappedAppHandler;
        });
    };
    Backend.prototype.hookPageFn = function (Page) {
        return hookLifeCycles(Page, [PageMethod.onLoad, PageMethod.onShow, PageMethod.onReady], this._pageLifeCycleHandlers());
    };
    Backend.prototype.hookComponentFn = function (Component) {
        return hookComponentLifeCycles(Component, [PageMethod.onLoad, PageMethod.onShow, PageMethod.onReady], this._pageLifeCycleHandlers());
    };
    Backend.prototype._pageLifeCycleHandlers = function () {
        var that = this;
        var callback = this.callback;
        return function (method, originalHandler) {
            function callOrigin(options) {
                try {
                    originalHandler === null || originalHandler === void 0 ? void 0 : originalHandler.call(this, options);
                }
                catch (err) {
                    log('PageLifeCycleMethodError', err);
                    callback({
                        ev_type: BackendEventType.JS_ERROR,
                        data: {
                            name: err.name,
                            message: err.message,
                            stack: err.stack,
                            rawMessage: String(err),
                        },
                        context: that.getContext(),
                    });
                }
            }
            function wrappedPageHandler(options) {
                var pid = getPid();
                log('Page', method, this.route, pid);
                switch (method) {
                    case PageMethod.onLoad: {
                        that.pid = pid;
                        that.hookSetData(this);
                        var onLoadStart = Date.now();
                        var pageSessionId = [that.deviceId, that.appSessionId, that.pid, onLoadStart].join('|');
                        that.pageSessionId = pageSessionId;
                        that.taskTracks[pageSessionId] = {
                            tasks: [],
                            timing: {
                                firstReqStart: onLoadStart,
                                firstReqEnd: 0,
                                firstLoadStart: onLoadStart,
                                firstLoadEnd: 0,
                            },
                            ready: false,
                            ended: false,
                        };
                        callback({
                            ev_type: BackendEventType.PAGE_SESSION,
                            data: {
                                pid: pid,
                                pageSessionId: pageSessionId,
                            },
                        });
                        callOrigin.call(this, options);
                        this[RANGERS_PAGE_SESSION_ID] = pageSessionId;
                        this[RANGERS_PAGE_TIMING] = {
                            onLoadStart: onLoadStart,
                            onLoadEnd: Date.now(),
                            onShowStart: 0,
                            onShowEnd: 0,
                            onReadyStart: 0,
                            onReadyEnd: 0,
                        };
                        return;
                    }
                    case PageMethod.onShow: {
                        var pageSessionId = this[RANGERS_PAGE_SESSION_ID];
                        that.pageSessionId = pageSessionId;
                        callback({
                            ev_type: BackendEventType.PAGE_SESSION,
                            data: {
                                pid: pid,
                                pageSessionId: pageSessionId,
                            },
                        });
                        var timing = this[RANGERS_PAGE_TIMING];
                        timing.onShowStart = timing.onShowStart || Date.now();
                        callOrigin.call(this, options);
                        timing.onShowEnd = timing.onShowEnd || Date.now();
                        return;
                    }
                    case PageMethod.onReady: {
                        var timing = this[RANGERS_PAGE_TIMING];
                        timing.onReadyStart = Date.now();
                        callOrigin.call(this, options);
                        timing.onReadyEnd = Date.now();
                        callback({
                            ev_type: BackendEventType.PAGE_ON_READY,
                            data: timing,
                        });
                        var taskTrack = that.getTaskTrack();
                        taskTrack.ready = true;
                        if (!taskTrack.timing.firstReqEnd && taskTrack.tasks.every(function (t) { return t.completed || t.type === 'SET_DATA'; })) {
                            taskTrack.timing.firstReqEnd = taskTrack.timing.firstReqStart; // no first req
                        }
                        if (taskTrack.tasks.every(function (t) { return t.completed; })) {
                            taskTrack.timing.firstLoadEnd = timing.onReadyEnd;
                            that.endTrack(taskTrack);
                        }
                        return;
                    }
                    default: {
                        break;
                    }
                }
                callOrigin.call(this, options);
            }
            return wrappedPageHandler;
        };
    };
    Backend.prototype.hookSdk = function () {
        this.hookRequest();
    };
    Backend.prototype.hookRequest = function () {
        var _this = this;
        var that = this;
        var callback = this.callback;
        hookObjectMethods(this.sdk, [SDKMethod.request], function (_, request) { return function (options) {
            var _a, _b;
            if (_this.shouldIgnoreUrl(options.url)) {
                return request(options);
            }
            log('request', (_a = options.method) !== null && _a !== void 0 ? _a : 'GET', options.url);
            var originalSuccess = options.success;
            var originalFail = options.fail;
            var originalComplete = options.complete;
            var req_size = getDataLength((_b = options.data) !== null && _b !== void 0 ? _b : '');
            var taskId = _this.addTask('REQUEST');
            var req_start_time = Date.now();
            var success = _this.captureContext(function (res) {
                var _a, _b;
                var req_end_time = Date.now();
                callback({
                    ev_type: BackendEventType.REQUEST,
                    data: {
                        req_url: options.url,
                        method: (_a = options.method) !== null && _a !== void 0 ? _a : 'GET',
                        err_msg: res.statusCode >= 300 ? serialize(res.data) : '',
                        req_status: 1,
                        req_start_time: req_start_time,
                        req_end_time: req_end_time,
                        resp_status: res.statusCode,
                        header: (_b = options.header) !== null && _b !== void 0 ? _b : {},
                        req_param: res.statusCode >= 300 ? serialize(options.data) : '',
                        req_size: req_size,
                        resp_size: getContentLength(res),
                        perf: res.profile
                            ? pick(res.profile, [
                                'redirectStart',
                                'redirectEnd',
                                'fetchStart',
                                'domainLookupStart',
                                'domainLookupEnd',
                                'connectStart',
                                'connectEnd',
                                'SSLconnectionStart',
                                'SSLconnectionEnd',
                                'requestStart',
                                'requestEnd',
                                'responseStart',
                                'responseEnd',
                                'rtt',
                                'httpRttEstimate',
                                'transportRttEstimate',
                            ])
                            : {
                                redirectStart: 0,
                                redirectEnd: 0,
                                fetchStart: 0,
                                domainLookupStart: 0,
                                domainLookupEnd: 0,
                                connectStart: 0,
                                connectEnd: 0,
                                SSLconnectionStart: 0,
                                SSLconnectionEnd: 0,
                                requestStart: 0,
                                requestEnd: 0,
                                responseStart: 0,
                                responseEnd: 0,
                                rtt: 0,
                                httpRttEstimate: 0,
                                transportRttEstimate: 0,
                            },
                    },
                    context: that.capturedContext,
                });
                that.taskStack.push(taskId);
                try {
                    originalSuccess === null || originalSuccess === void 0 ? void 0 : originalSuccess.call(this, res);
                }
                finally {
                    that.taskStack.pop();
                }
            });
            var fail = _this.captureContext(function (res) {
                var _a, _b, _c;
                var req_end_time = Date.now();
                callback({
                    ev_type: BackendEventType.REQUEST,
                    data: {
                        req_url: options.url,
                        method: (_a = options.method) !== null && _a !== void 0 ? _a : 'GET',
                        err_msg: (_b = res.errMsg) !== null && _b !== void 0 ? _b : '',
                        req_status: 0,
                        req_start_time: req_start_time,
                        req_end_time: req_end_time,
                        resp_status: 0,
                        req_param: serialize(options.data),
                        header: (_c = options.header) !== null && _c !== void 0 ? _c : {},
                        req_size: req_size,
                        resp_size: 0,
                        perf: {},
                    },
                    context: that.capturedContext,
                });
                that.taskStack.push(taskId);
                try {
                    originalFail === null || originalFail === void 0 ? void 0 : originalFail.call(this, res);
                }
                finally {
                    that.taskStack.pop();
                }
            });
            var complete = _this.captureContext(function (res) {
                that.taskStack.push(taskId);
                try {
                    originalComplete === null || originalComplete === void 0 ? void 0 : originalComplete.call(this, res);
                }
                finally {
                    that.taskStack.pop();
                }
                that.endTask(taskId, Date.now());
            });
            var newOptions = __assign(__assign({}, options), { success: success, fail: fail, complete: complete });
            return request(newOptions);
        }; });
    };
    Backend.prototype.shouldIgnoreUrl = function (url) {
        return url === this.reportUrl;
    };
    Backend.prototype.hookSetData = function (page) {
        var that = this;
        hookObjectMethods(page, ['setData'], function (_, setData) {
            return function wrappedSetData(data, callback) {
                var _this = this;
                log('setData', data, this.route, that.pid, that.pageSessionId);
                var originalCallback = callback;
                var taskId = that.addTask('SET_DATA');
                var setDataStart = Date.now();
                var wrappedCallback = that.captureContext(function () {
                    var setDataEnd = Date.now();
                    var dataJson = serialize(data);
                    that.callback({
                        ev_type: BackendEventType.SET_DATA,
                        data: {
                            setDataStart: setDataStart,
                            setDataEnd: setDataEnd,
                            setDataSize: strSize(dataJson),
                            data: dataJson,
                        },
                        context: that.capturedContext,
                    });
                    that.taskStack.push(taskId);
                    try {
                        originalCallback === null || originalCallback === void 0 ? void 0 : originalCallback.call(_this);
                    }
                    catch (err) {
                        log('SetDataCallbackError', err);
                        that.callback({
                            ev_type: BackendEventType.JS_ERROR,
                            data: {
                                name: err.name,
                                message: err.message,
                                stack: err.stack,
                                rawMessage: String(err),
                            },
                            context: that.getContext(),
                        });
                    }
                    finally {
                        that.taskStack.pop();
                    }
                    that.endTask(taskId, setDataEnd);
                });
                setData.call(this, data, wrappedCallback);
            };
        });
    };
    Backend.prototype.collectDeviceId = function () {
        var deviceId = this.getStorage(DEVICE_ID_STORAGE_KEY);
        if (!deviceId) {
            deviceId = uuid();
            this.setStorage(DEVICE_ID_STORAGE_KEY, deviceId);
        }
        this.deviceId = deviceId;
        this.callback({
            ev_type: BackendEventType.DEVICE_ID,
            data: {
                deviceId: deviceId,
            },
        });
    };
    Backend.prototype.getContext = function () {
        var _a;
        return (_a = this.capturedContext) !== null && _a !== void 0 ? _a : this.currentContext();
    };
    Backend.prototype.currentContext = function () {
        return {
            pid: this.pid,
            pageSessionId: this.pageSessionId,
            context: this.options.context,
        };
    };
    Backend.prototype.captureContext = function (fn) {
        var that = this;
        var context = this.getContext();
        // log('captureContext', context)
        return function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            // log('call with capturedContext', context)
            // log('currentContext', that.currentContext())
            that.capturedContext = context;
            var res;
            try {
                res = fn.apply(this, args);
            }
            catch (err) {
                log('ContextCapturedMethodError', err);
                that.callback({
                    ev_type: BackendEventType.JS_ERROR,
                    data: {
                        name: err.name,
                        message: err.message,
                        stack: err.stack,
                        rawMessage: String(err),
                    },
                    context: context,
                });
            }
            finally {
                that.capturedContext = null;
            }
            return res;
        };
    };
    Backend.prototype.addTask = function (type) {
        var taskTrack = this.getTaskTrack();
        if (!taskTrack || taskTrack.ended) {
            return 0;
        }
        var parentTaskId = this.taskStack.length && this.taskStack[this.taskStack.length - 1];
        if (taskTrack.ready && (!parentTaskId || !taskTrack.tasks.some(function (t) { return t.id === parentTaskId; }))) {
            return 0;
        }
        var id = taskTrack.tasks.length + 1;
        log('addTask', type, id);
        taskTrack.tasks.push({
            id: id,
            type: type,
            completed: false,
        });
        return id;
    };
    Backend.prototype.endTask = function (id, now) {
        var taskTrack = this.getTaskTrack();
        if (id <= 0 || !taskTrack || taskTrack.ended) {
            return;
        }
        var task = taskTrack.tasks[id - 1];
        log('endTask', id, task);
        task.completed = true;
        if (!taskTrack.timing.firstReqEnd && task.type === 'REQUEST') {
            taskTrack.timing.firstReqEnd = now;
        }
        if (taskTrack.tasks.every(function (t) { return t.completed; })) {
            taskTrack.timing.firstLoadEnd = now;
            this.endTrack(taskTrack);
        }
    };
    Backend.prototype.endTrack = function (taskTrack) {
        log('endTrack', taskTrack);
        if (!taskTrack.ready) {
            return;
        }
        taskTrack.ended = true;
        this.callback({
            ev_type: BackendEventType.PAGE_FIRST_LOAD,
            data: taskTrack.timing,
            context: this.getContext(),
        });
    };
    Backend.prototype.getTaskTrack = function () {
        var context = this.getContext();
        var pageSessionId = context.pageSessionId;
        return this.taskTracks[pageSessionId];
    };
    return Backend;
}());
export { Backend };
//# sourceMappingURL=base.js.map